import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/components/dialog';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { apiErrorMessage } from '@/shared/api-client';
import {
  forceLogout as forceLogoutUser,
  resetPassword as resetUserPassword,
  useUpdateUser,
  type User,
} from './api';
import { UserAssignments } from './UserAssignments';
import { UserPosPins } from './UserPosPins';

interface Props {
  user: User | null;
  onOpenChange: (open: boolean) => void;
  onActioned: (message: string) => void;
}

type Confirm = 'deactivate' | 'forceLogout' | 'resetPassword' | null;

export function ManageUserDialog({ user, onOpenChange, onActioned }: Props) {
  const { t } = useTranslation('common');
  const updateUser = useUpdateUser();
  const forceLogout = useMutation({ mutationFn: forceLogoutUser });
  const resetPassword = useMutation({ mutationFn: resetUserPassword });

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  useEffect(() => {
    setEmail(user?.email ?? '');
    setDisplayName(user?.displayName ?? '');
    setError(null);
    setConfirm(null);
    setTempPassword(null);
  }, [user]);

  if (!user) return null;

  async function run(action: () => Promise<unknown>, successKey: string) {
    setError(null);
    try {
      await action();
      onActioned(t(successKey));
      onOpenChange(false);
    } catch (caught) {
      setError(apiErrorMessage(t, 'users', caught));
    }
  }

  const saveDetails = (event: React.FormEvent) => {
    event.preventDefault();
    const next = {
      email: email.trim() !== user.email ? email.trim() : undefined,
      displayName:
        (displayName.trim() || '') !== (user.displayName ?? '') ? displayName.trim() : undefined,
    };
    if (next.email === undefined && next.displayName === undefined) {
      onOpenChange(false);
      return;
    }
    void run(() => updateUser.mutateAsync({ id: user.id, input: next }), 'users.toast.updated');
  };

  async function runReset() {
    setError(null);
    try {
      const result = await resetPassword.mutateAsync(user!.id);
      setTempPassword(result.tempPassword);
      setConfirm(null);
    } catch (caught) {
      setError(apiErrorMessage(t, 'users', caught));
    }
  }

  const busy = updateUser.isPending || forceLogout.isPending || resetPassword.isPending;

  return (
    <Dialog open={user != null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('users.manage.title')}</DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>

        <form onSubmit={saveDetails} className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="manage-user-email">{t('users.fields.email')}</Label>
            <Input
              id="manage-user-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="manage-user-name">{t('users.fields.displayName')}</Label>
            <Input
              id="manage-user-name"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="submit" disabled={busy}>
              {t('users.manage.save')}
            </Button>
          </DialogFooter>
        </form>

        <div className="mt-2 grid gap-3 border-t pt-4">
          <p className="text-sm font-medium">{t('users.manage.dangerZone')}</p>

          {confirm === null && (
            <div className="flex flex-wrap gap-2">
              {user.isActive ? (
                <Button
                  variant="destructive"
                  disabled={busy}
                  onClick={() => setConfirm('deactivate')}
                >
                  {t('users.actions.deactivate')}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() =>
                    void run(
                      () => updateUser.mutateAsync({ id: user.id, input: { isActive: true } }),
                      'users.toast.activated',
                    )
                  }
                >
                  {t('users.actions.activate')}
                </Button>
              )}
              <Button variant="outline" disabled={busy} onClick={() => setConfirm('forceLogout')}>
                {t('users.actions.forceLogout')}
              </Button>
              <Button variant="outline" disabled={busy} onClick={() => setConfirm('resetPassword')}>
                {t('users.actions.resetPassword')}
              </Button>
            </div>
          )}

          {tempPassword && (
            <TempPasswordPanel password={tempPassword} onDismiss={() => setTempPassword(null)} />
          )}

          {confirm === 'deactivate' && (
            <ConfirmRow
              message={t('users.confirm.deactivate')}
              confirmLabel={t('users.actions.deactivate')}
              busy={busy}
              onCancel={() => setConfirm(null)}
              onConfirm={() =>
                void run(
                  () => updateUser.mutateAsync({ id: user.id, input: { isActive: false } }),
                  'users.toast.deactivated',
                )
              }
            />
          )}

          {confirm === 'forceLogout' && (
            <ConfirmRow
              message={t('users.confirm.forceLogout')}
              confirmLabel={t('users.actions.forceLogout')}
              busy={busy}
              onCancel={() => setConfirm(null)}
              onConfirm={() =>
                void run(() => forceLogout.mutateAsync(user.id), 'users.toast.forcedLogout')
              }
            />
          )}

          {confirm === 'resetPassword' && (
            <ConfirmRow
              message={t('users.confirm.resetPassword')}
              confirmLabel={t('users.actions.resetPassword')}
              busy={busy}
              onCancel={() => setConfirm(null)}
              onConfirm={() => void runReset()}
            />
          )}
        </div>

        <UserPosPins userId={user.id} />

        <UserAssignments userId={user.id} />
      </DialogContent>
    </Dialog>
  );
}

function ConfirmRow({
  message,
  confirmLabel,
  busy,
  onCancel,
  onConfirm,
}: {
  message: string;
  confirmLabel: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation('common');
  return (
    <div className="grid gap-2 rounded-md border border-destructive/40 p-3">
      <p className="text-sm">{message}</p>
      <div className="flex gap-2">
        <Button variant="destructive" disabled={busy} onClick={onConfirm}>
          {confirmLabel}
        </Button>
        <Button variant="outline" disabled={busy} onClick={onCancel}>
          {t('actions.cancel')}
        </Button>
      </div>
    </div>
  );
}

function TempPasswordPanel({ password, onDismiss }: { password: string; onDismiss: () => void }) {
  const { t } = useTranslation('common');
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
    } catch {
      // Clipboard may be unavailable (insecure context); the value is still shown to copy by hand.
    }
  };

  return (
    <div className="grid gap-2 rounded-md border border-amber-500/50 bg-amber-50 p-3 dark:bg-amber-950/20">
      <p className="text-sm font-medium">{t('users.resetPassword.title')}</p>
      <p className="text-muted-foreground text-sm">{t('users.resetPassword.warning')}</p>
      <code
        data-testid="temp-password"
        className="bg-background rounded border px-2 py-1 font-mono text-sm break-all"
      >
        {password}
      </code>
      <div className="flex gap-2">
        <Button variant="secondary" onClick={() => void copy()}>
          {copied ? t('users.resetPassword.copied') : t('users.resetPassword.copy')}
        </Button>
        <Button variant="outline" onClick={onDismiss}>
          {t('users.resetPassword.dismiss')}
        </Button>
      </div>
    </div>
  );
}
